import type {
  ChildSummary,
  DecoratedMutationRequest,
  EntityId,
  FamilySummary,
  MutationRequest,
  MutationRequestCollection,
  ParentSummary,
  RelatedEntityIds,
  UserSummary,
} from "../types/mutationRequests.types";

const UUID_PATTERN = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

export const looksLikeUuid = (value: unknown): value is EntityId => {
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  return Boolean(trimmed && UUID_PATTERN.test(trimmed));
};

export const normalizeEntityIds = (identifiers: readonly unknown[] | unknown): EntityId[] => {
  const values = Array.isArray(identifiers) ? identifiers : [identifiers];
  return Array.from(
    new Set(
      values
        .map((id) => (id != null ? String(id).trim() : ""))
        .filter(looksLikeUuid)
    )
  );
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const stringValue = (value: unknown): string => (typeof value === "string" ? value.trim() : "");

const firstString = (values: unknown[]): string | null => {
  for (const value of values) {
    const trimmed = stringValue(value);
    if (trimmed) return trimmed;
  }
  return null;
};

const joinedName = (first: unknown, last: unknown): string => [first, last].filter(Boolean).join(" ").trim();

export const getUserDisplayName = (user: unknown): string | null => {
  if (!isRecord(user)) return null;
  return firstString([
    joinedName(user.firstName, user.lastName),
    joinedName(user.first_name, user.last_name),
    user.displayName,
    user.display_name,
    user.fullName,
    user.fullname,
    user.username,
    user.email,
  ]);
};

export const getChildDisplayName = (child: unknown): string | null => {
  if (!isRecord(child)) return null;
  return firstString([
    joinedName(child.firstName, child.lastName),
    joinedName(child.first_name, child.last_name),
    child.displayName,
    child.display_name,
    child.fullName,
    child.fullname,
    child.callName,
    child.nickname,
  ]);
};

export const getParentDisplayName = (parent: unknown): string | null => {
  if (!isRecord(parent)) return null;
  return firstString([
    joinedName(parent.firstName, parent.lastName),
    joinedName(parent.first_name, parent.last_name),
    parent.displayName,
    parent.display_name,
    parent.fullName,
    parent.fullname,
    parent.email,
  ]);
};

export const getFamilyDisplayName = (family: unknown): string | null => {
  if (!isRecord(family)) return null;
  return firstString([family.familyName, family.name, family.displayName, family.display_name]);
};

const parsePayloadForTraversal = (payloadRaw: unknown): unknown => {
  if (typeof payloadRaw !== "string") return payloadRaw;
  try {
    return JSON.parse(payloadRaw) as unknown;
  } catch {
    return null;
  }
};

const addUuid = (target: Set<EntityId>, value: unknown) => {
  if (typeof value !== "string") return;
  const trimmed = value.trim();
  if (looksLikeUuid(trimmed)) target.add(trimmed);
};

export const extractRelatedIds = (payloadRaw: unknown): RelatedEntityIds => {
  const childIds = new Set<EntityId>();
  const familyIds = new Set<EntityId>();
  const visited = new WeakSet<object>();

  const visit = (value: unknown) => {
    if (!value) return;
    if (Array.isArray(value)) {
      value.forEach(visit);
      return;
    }
    if (!isRecord(value)) return;
    if (visited.has(value)) return;
    visited.add(value);

    addUuid(childIds, value.childId ?? value.child_id ?? value.childUUID ?? value.childUuid);
    addUuid(familyIds, value.familyId ?? value.family_id ?? value.familyUUID ?? value.familyUuid);

    if (isRecord(value.child)) addUuid(childIds, value.child.id);
    if (isRecord(value.family)) addUuid(familyIds, value.family.id);

    Object.values(value).forEach(visit);
  };

  visit(parsePayloadForTraversal(payloadRaw));

  return {
    childIds: Array.from(childIds),
    familyIds: Array.from(familyIds),
  };
};

export const decorateRequest = (request: MutationRequest): DecoratedMutationRequest => {
  const related = extractRelatedIds(request.payload);
  return {
    ...request,
    relatedChildIds: related.childIds,
    relatedFamilyIds: related.familyIds,
  };
};

const reviewedTime = (request: MutationRequest): number => {
  const date = new Date(String(request.reviewedAt || 0));
  const time = date.getTime();
  return Number.isNaN(time) ? 0 : time;
};

export const toMutationRequestCollection = (requests: MutationRequest[]): MutationRequestCollection => {
  const pending = requests.filter((request) => String(request.status || "").toUpperCase() === "PENDING");
  const history = requests
    .filter((request) => String(request.status || "").toUpperCase() !== "PENDING")
    .sort((a, b) => reviewedTime(b) - reviewedTime(a));

  return {
    pending: pending.map(decorateRequest),
    history: history.map(decorateRequest),
  };
};

export const userSummaryFromResponse = (id: EntityId, data: unknown): UserSummary => {
  const raw = isRecord(data) && "user" in data ? data.user : data;
  return { id, displayName: getUserDisplayName(raw) || id, raw };
};

export const parentSummaryFromResponse = (id: EntityId, data: unknown): ParentSummary => {
  const record = isRecord(data) ? data : {};
  return {
    id,
    displayName: getParentDisplayName(record) || id,
    email: stringValue(record.email ?? record.emailAddress),
    phone: stringValue(record.phone ?? record.phoneNumber ?? record.mobile ?? record.mobilePhone),
    raw: data,
  };
};

export const childSummaryFromResponse = (id: EntityId, data: unknown): ChildSummary => ({
  id,
  displayName: getChildDisplayName(data) || id,
  raw: data,
});

export const familySummaryFromResponse = (id: EntityId, data: unknown): FamilySummary => ({
  id,
  displayName: getFamilyDisplayName(data) || id,
  raw: data,
});

export const fallbackLookupEntry = (kind: "user" | "parent" | "child" | "family", id: EntityId) => {
  if (kind === "parent") return { id, displayName: id, email: "", phone: "", raw: null };
  return { id, displayName: id, raw: null };
};

export const getApiErrorStatus = (error: unknown): number | undefined => {
  if (!isRecord(error)) return undefined;
  const response = isRecord(error.response) ? error.response : null;
  return typeof response?.status === "number" ? response.status : undefined;
};

export const getApiErrorMessage = (error: unknown, fallback: string): string => {
  if (!isRecord(error)) return fallback;
  const response = isRecord(error.response) ? error.response : null;
  const data = isRecord(response?.data) ? response.data : null;
  return stringValue(data?.message) || stringValue(data?.error) || stringValue(error.message) || fallback;
};
